import type { InputFile } from "@rhema/client";

// Reads what someone dropped: files, and folders such as an unzipped export, with each file's path.

type Entry = {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
  file?: (ok: (f: File) => void, fail: (e: unknown) => void) => void;
  createReader?: () => { readEntries: (ok: (entries: Entry[]) => void, fail: (e: unknown) => void) => void };
};

/** The most files one drop reads, so a dropped home folder cannot stall the app. */
export const MAX_DROPPED_FILES = 20_000;

async function walk(entry: Entry, out: InputFile[]): Promise<void> {
  if (out.length >= MAX_DROPPED_FILES) return;
  if (entry.isFile && entry.file) {
    const file = await new Promise<File>((ok, fail) => entry.file!(ok, fail));
    out.push({ file, path: entry.fullPath.replace(/^\//, "") });
    return;
  }
  if (entry.isDirectory && entry.createReader) {
    const reader = entry.createReader();
    // readEntries returns the folder in pieces until it returns nothing.
    for (;;) {
      const batch = await new Promise<Entry[]>((ok, fail) => reader.readEntries(ok, fail));
      if (!batch.length) return;
      for (const child of batch) await walk(child, out);
    }
  }
}

export async function filesFromDrop(data: DataTransfer | null): Promise<InputFile[]> {
  if (!data) return [];
  const items = Array.from(data.items ?? []);
  const entries = items
    .map((item) =>
      item.kind === "file" ? (item as { webkitGetAsEntry?: () => Entry | null }).webkitGetAsEntry?.() : null,
    )
    .filter((e): e is Entry => Boolean(e));
  if (!entries.length) return Array.from(data.files ?? []).map((file) => ({ file }));
  const out: InputFile[] = [];
  for (const entry of entries) await walk(entry, out);
  return out;
}

/** Files from an input, with their folder paths when a folder was chosen. */
export function filesFromInput(list: FileList | null): InputFile[] {
  return Array.from(list ?? []).map((file) => ({
    file,
    ...(file.webkitRelativePath ? { path: file.webkitRelativePath } : {}),
  }));
}
